import type { Account } from "@eai-toolkit/client";
import { Box } from "@mui/material";
import { HTTP_FORBIDDEN, HTTP_NOT_FOUND } from "@store/api/types";
import React, { useEffect, useMemo, useRef } from "react";

import { AccountSelector } from "@components/AccountSelector/AccountSelector";
import { useValidatedResourceField } from "@components/ValidationContext/ValidationContext";

import { useGetAccountByIdQuery } from "../../../store/api/accountsApi";
import { useAccountsForJobSubmit } from "../../../store/hooks/useAccountsForJobSubmit";
import { fieldStyle } from "./NewJobFormLayout";

const JOB_SUBMIT_PERMISSION = "job:new";

type Props = {
  value: string | null;
  onUpdate: (value: string | null) => void;
  /** Reports the resolved Account object once known, for display elsewhere (e.g. an overview label). */
  onAccountResolved?: (account: Account | null) => void;
};

const AccountField = React.memo(function AccountField({
  value,
  onUpdate,
  onAccountResolved,
}: Props) {
  const { accountsMap } = useAccountsForJobSubmit();

  // Fetch single account if value is set but not in the (job:new-filtered) list
  const shouldFetchSingleAccount = Boolean(value && !accountsMap.has(value));
  const { data: singleAccountData, error: singleAccountError } =
    useGetAccountByIdQuery(
      { id: value || "", permission: JOB_SUBMIT_PERMISSION },
      { skip: !shouldFetchSingleAccount },
    );

  const resolvedAccount = useMemo((): Account | null => {
    if (!value) return null;
    return accountsMap.get(value) ?? singleAccountData?.account ?? null;
  }, [value, accountsMap, singleAccountData]);

  useEffect(() => {
    onAccountResolved?.(resolvedAccount);
  }, [resolvedAccount, onAccountResolved]);

  // If the parent passed a fullName (or any non-ID identifier) we can resolve,
  // normalize the controlled value to the canonical account ID.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  useEffect(() => {
    if (resolvedAccount && resolvedAccount.id !== value) {
      onUpdateRef.current(resolvedAccount.id);
    }
  }, [resolvedAccount, value]);

  const errorMessage = useMemo(() => {
    if (!value) {
      return "Account selection is required";
    }

    // In the permission-filtered accounts list already — no error
    if (accountsMap.has(value)) {
      return null;
    }

    if (singleAccountError) {
      if (
        typeof singleAccountError === "object" &&
        "status" in singleAccountError
      ) {
        const status = (singleAccountError as { status: number | string })
          .status;
        if (status === HTTP_FORBIDDEN) return "You cannot access this account";
        if (status === HTTP_NOT_FOUND)
          return "Account does not appear to exist";
      }
      return "Error loading account";
    }

    if (singleAccountData?.permissionStatus) {
      if (singleAccountData.permissionStatus === "not_allowed") {
        return "Job creation not allowed for account";
      }
      if (singleAccountData.permissionStatus !== "ok") {
        return "Error checking account permissions";
      }
    }

    return null;
  }, [value, accountsMap, singleAccountData, singleAccountError]);

  const { visibleErrorMessage, handleFocus, handleBlur } =
    useValidatedResourceField({
      instanceId: "account",
      valueForValidation: value ?? "",
      error: errorMessage,
    });

  return (
    <Box onFocus={handleFocus} onBlur={handleBlur}>
      <AccountSelector
        value={value}
        onChange={onUpdate}
        permission={JOB_SUBMIT_PERMISSION}
        required
        error={visibleErrorMessage}
        placeholder="Select an account..."
        sx={fieldStyle}
      />
    </Box>
  );
});

export default AccountField;
